import { Component, inject, signal, input, OnInit, PLATFORM_ID, computed } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { DialogModule } from 'primeng/dialog';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ClientService, ClientPortalUser } from '../../../services/client';
import { ToastService } from '../../../services/toast';

/**
 * Client portal access: the logins a client's own staff use to sign in to the client portal.
 * An Arogya admin bootstraps the first client-admin here and can reset a forgotten password.
 *
 * Passwords are write-only — sent once over TLS, hashed server-side, never read back. The
 * server also forces MustChangePassword on a new user, so whatever is set here is temporary.
 */
@Component({
  selector: 'app-client-portal-users',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    TableModule,
    TagModule,
    InputTextModule,
    PasswordModule,
    DialogModule,
    ToggleSwitchModule,
  ],
  templateUrl: './client-portal-users.html',
  styleUrls: ['./client-portal-users.css'],
})
export class ClientPortalUsers implements OnInit {
  readonly clientId = input.required<number>();

  private svc = inject(ClientService);
  private fb = inject(FormBuilder);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);

  readonly rows = signal<ClientPortalUser[]>([]);
  readonly loading = signal(false);
  readonly saving = signal(false);

  readonly showCreate = signal(false);
  readonly showReset = signal(false);
  readonly resetTarget = signal<ClientPortalUser | null>(null);

  /** True once the client has at least one admin login — drives the empty-state call to action. */
  readonly hasAdmin = computed(() => this.rows().some((u) => u.isClientAdmin && u.isActive));

  createForm: FormGroup = this.fb.group({
    username: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(60)]],
    fullName: ['', [Validators.maxLength(120)]],
    email: ['', [Validators.email]],
    mobile: ['', [Validators.pattern(/^\d{10}$/)]],
    // 8 is the server's floor (Admin/Users/ResetPassword rejects anything shorter).
    password: ['', [Validators.required, Validators.minLength(8)]],
    isClientAdmin: [true],
    isActive: [true],
  });

  resetForm: FormGroup = this.fb.group({
    password: ['', [Validators.required, Validators.minLength(8)]],
  });

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.load();
  }

  load() {
    this.loading.set(true);
    this.svc.portalUsers(this.clientId()).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        this.rows.set(res?.data ?? []);
      },
      error: () => {
        this.loading.set(false);
        this.rows.set([]);
      },
    });
  }

  openCreate() {
    this.createForm.reset({ isClientAdmin: !this.hasAdmin(), isActive: true, username: '', password: '' });
    this.showCreate.set(true);
  }

  create() {
    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      return;
    }
    this.saving.set(true);
    this.svc.portalUserCreate(this.clientId(), this.createForm.value).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not create the login.');
          return;
        }
        this.toast.success('Portal login created. The user must change this password at first sign-in.');
        this.showCreate.set(false);
        this.load();
      },
      error: () => {
        this.saving.set(false);
        this.toast.error('Could not create the login.');
      },
    });
  }

  openReset(u: ClientPortalUser) {
    this.resetTarget.set(u);
    this.resetForm.reset({ password: '' });
    this.showReset.set(true);
  }

  resetPassword() {
    const u = this.resetTarget();
    if (!u || this.resetForm.invalid) {
      this.resetForm.markAllAsTouched();
      return;
    }
    this.saving.set(true);
    this.svc.portalUserResetPassword(this.clientId(), u.id, this.resetForm.value.password).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not reset the password.');
          return;
        }
        this.toast.success(`Password reset for ${u.username}. Share it securely — it cannot be viewed again.`);
        this.showReset.set(false);
        this.load();
      },
      error: () => {
        this.saving.set(false);
        this.toast.error('Could not reset the password.');
      },
    });
  }

  invalid(form: FormGroup, name: string): boolean {
    const c = form.get(name);
    return !!c && c.invalid && (c.touched || c.dirty);
  }
}
